import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle, XCircle, Sparkles } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function MagicLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [error, setError] = useState('');

  const token = searchParams.get('token');
  const isOnboarding = searchParams.get('onboarding') === 'true';
  const isReturning = searchParams.get('returning') === 'true';

  useEffect(() => {
    const processToken = async () => {
      if (!token) {
        setStatus('error');
        setError('No login token provided');
        return;
      }

      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/shopify-billing/magic-login?token=${token}`
        );

        const { access_token, user } = response.data;

        // Store auth data
        localStorage.setItem('token', access_token);
        localStorage.setItem('user', JSON.stringify(user));

        setStatus('success');

        // Redirect after brief success message
        setTimeout(() => {
          if (isOnboarding) {
            navigate('/onboarding');
          } else if (isReturning) {
            navigate('/dashboard');
          } else {
            navigate('/dashboard');
          }
        }, 1500);

      } catch (err) {
        console.error('Magic login error:', err);
        setStatus('error');
        setError(err.response?.data?.detail || 'Login failed. Please try again.');
      }
    };

    processToken();
  }, [token, navigate, isOnboarding, isReturning]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 max-w-md w-full text-center border border-white/20">
        {status === 'processing' && (
          <>
            <div className="relative mb-6">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center animate-pulse">
                <Sparkles className="w-10 h-10 text-white" />
              </div>
              <Loader2 className="w-8 h-8 text-purple-400 animate-spin absolute -bottom-2 left-1/2 -translate-x-1/2" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">
              Logging you in...
            </h2>
            <p className="text-gray-300">
              Please wait while we verify your credentials
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">
              Welcome{isReturning ? ' Back' : ''}!
            </h2>
            <p className="text-gray-300">
              {isOnboarding 
                ? "Let's get your account set up..." 
                : 'Redirecting to your dashboard...'}
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center mb-6">
              <XCircle className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">
              Login Failed
            </h2>
            <p className="text-red-300 mb-6">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-3 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
            >
              Go to Login Page
            </button>
          </>
        )}
      </div>
    </div>
  );
}
